import { useEffect, useRef, useState } from "react";

import { LOCALES } from "@/libraries/web-framework/src/localization/localization.constants";

import { AnchorLinkProps } from "./side-nav-link/src/side-nav-link";

export const getCurrRoute = () => {
    let currRoute = window.location.pathname;
    if (currRoute === "/uniform-preview") {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const pathParam = urlParams.get("path");
        currRoute = pathParam ? decodeURIComponent(pathParam) : "";
    }

    // Remove locale
    for (const locale of LOCALES) {
        if (currRoute.startsWith(`/${locale}/`)) {
            currRoute = currRoute.slice(`/${locale}`.length);
            break;
        }
    }

    return currRoute;
};

// Hook to track which h2 element is at the top of the viewport
export const useHeadingAtTop = (anchors?: AnchorLinkProps[]) => {
    const [headingIdAtTop, setHeadingIdAtTop] = useState<string | null>(null);
    const [validAnchorIds, setValidAnchorIds] = useState<Set<string>>(
        new Set()
    );
    const headingIdRef = useRef<string | null>(null);

    useEffect(() => {
        const bodyContent = document.getElementById("body-content");
        if (!anchors || anchors.length === 0 || !bodyContent) {
            setValidAnchorIds((prevValidIds) => {
                if (prevValidIds.size > 0) {
                    return new Set();
                }
                return prevValidIds;
            });
            return;
        }

        // Handle scroll to anchored section
        document.documentElement.style.scrollBehavior = "smooth";
        const headerHeightStr = getComputedStyle(document.documentElement)
            .getPropertyValue("--wae-header-height")
            .trim();
        document.documentElement.style.scrollPaddingTop =
            headerHeightStr || "80px";

        // Used below to calculate distance from top of viewport
        const headerHeight =
            parseInt(headerHeightStr.replace("px", ""), 10) || 80;

        const anchorHeadings = anchors
            .map((anchor) => document.getElementById(`${anchor.id}`))
            .filter(
                (heading): heading is HTMLElement =>
                    heading instanceof HTMLElement
            );

        // Check which heading is at the top of the viewport
        const checkHeadingAtTop = () => {
            const viewportTop = window.scrollY + headerHeight;
            let headingAtTop: HTMLElement | null = null;

            for (const heading of anchorHeadings) {
                const rect = heading?.getBoundingClientRect();
                const headingTop = window.scrollY + (rect?.top ?? 0);

                if (
                    headingTop >= viewportTop - 100 &&
                    headingTop <= viewportTop + 100
                ) {
                    headingAtTop = heading;
                }
            }

            if (headingAtTop) {
                const newId = headingAtTop.id;
                if (newId !== headingIdRef.current) {
                    headingIdRef.current = newId;
                    setHeadingIdAtTop(newId);
                }
            }
        };

        // Create intersection observer to detect when headings reach the top
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const rect = entry.boundingClientRect;
                        const viewportTop = headerHeight;

                        // Check if heading is at the top of the viewport (within 100px)
                        if (
                            rect.top >= viewportTop - 100 &&
                            rect.top <= viewportTop + 100
                        ) {
                            const id = (entry.target as HTMLElement).id;
                            if (id !== headingIdRef.current) {
                                headingIdRef.current = id;
                                setHeadingIdAtTop(id);
                            }
                        }
                    }
                });
            },
            {
                root: null,
                rootMargin: `${-headerHeight}px 0px 0px 0px`,
                threshold: [0, 0.1, 0.5, 0.9, 1]
            }
        );

        // Observe all headings
        anchorHeadings.forEach((heading) => observer.observe(heading));

        // Scroll to anchor element
        const scrollToAnchor = (hash: string) => {
            if (!hash) return;
            const targetHeading = anchorHeadings.find((h) => h.id === hash);
            if (targetHeading) {
                // Use requestAnimationFrame to ensure DOM is ready
                requestAnimationFrame(() => {
                    targetHeading.scrollIntoView({ behavior: "smooth" });
                });
            }
        };

        // Check on scroll
        const handleScroll = () => {
            checkHeadingAtTop();
        };

        // Handle hash changes (e.g., when clicking anchor links)
        const handleHashChange = () => {
            const hash = window.location.hash.slice(1); // Remove '#'
            scrollToAnchor(hash);
        };

        // Initial check
        checkHeadingAtTop();

        // Scroll to anchor on initial page load if hash is present
        const initialHash = window.location.hash.slice(1);
        if (initialHash) {
            // Use a small timeout to ensure page is fully rendered
            setTimeout(() => {
                scrollToAnchor(initialHash);
            }, 100);
        }

        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll, { passive: true });
        window.addEventListener("hashchange", handleHashChange);

        // Cleanup
        return () => {
            anchorHeadings.forEach((heading) => observer.unobserve(heading));
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
            window.removeEventListener("hashchange", handleHashChange);
            document.documentElement.style.scrollBehavior = "";
            document.documentElement.style.scrollPaddingTop = "";
        };
    }, [anchors]);

    // Update URL anchor when heading changes
    useEffect(() => {
        if (headingIdAtTop) {
            const currentHash = window.location.hash.slice(1); // Remove '#'
            if (currentHash !== headingIdAtTop) {
                const newUrl = `${window.location.pathname}${window.location.search}#${headingIdAtTop}`;
                window.history.replaceState(null, "", newUrl); // update URL without adding to history
            }
        }
    }, [headingIdAtTop]);

    return { headingIdAtTop, validAnchorIds };
};
